// Barre « Respect du plan » : les mêmes cases pour une journée (Dispatch vs Geotab) et pour une période (Calibrage, Tableau de bord).
// Rangée principale : arrêts, ordre, durées moyennes (route, chargement, livraison), hors plan et heures. Ligne du dessous :
// Chargés PM, préchargements et heures demandées. Arrêts : taux global et détail par type ; pourcentages par période, nombres par journée.
import { fmtDelta, fmtDuration } from "./geotabFormat.js";
import { toleranceLabel } from "./geotabPreferences.js";

const pct = (done, total) => (total ? Math.round((100 * done) / total) : null);
const plural = (n, word) => `${n} ${word}${n > 1 ? "s" : ""}`;
const perDay = (minutes, days) => (days ? minutes / days : null);
const hoursGap = (h) => (h?.days ? (h.real - h.planned) / h.days : null);

function share(part, period, { planned = part.planned, done = part.done } = {}) {
  if (!planned) return "—";
  return period ? `${pct(done, planned)} %` : `${done} / ${planned}`;
}

// Arrêts non faits : « tous faits » quand rien ne manque, « aucun prévu » sans arrêt de ce type.
function missingText(part, word, all, none) {
  if (!part.planned) return none;
  const missing = part.planned - part.done;
  return missing ? `${missing} ${word}${missing > 1 ? "s" : ""}` : all;
}

// Chaque arrêt prévu a le même poids, qu'il s'agisse d'un chargement ou d'une livraison.
function combinedStops(bar) {
  return { done: bar.loads.done + bar.deliveries.done, planned: bar.loads.planned + bar.deliveries.planned };
}

export function planBarStops(bar) {
  return [
    { key: "loads", label: "Chargements", part: bar.loads, word: "non fait", all: "tous faits", none: "aucun prévu" },
    { key: "deliveries", label: "Livraisons", part: bar.deliveries, word: "non faite", all: "toutes faites", none: "aucune prévue" },
  ].map(({ part, word, all, none, ...item }) => ({
    ...item, value: share(part, true),
    count: part.planned ? `${part.done} / ${part.planned}` : null,
    missing: missingText(part, word, all, none),
  }));
}

// Moyennes prévue et réelle des étapes mesurées des deux côtés, comme les totaux de « Durées par étape ». Le prévu est celui des
// heures affichées : rejoué avec les réglages actuels au Tableau de bord et au Calibrage. En simulation : l'écart avant → après.
// Sous le grand chiffre : la part des visites à la tolérance près. L'écart typique (moyen, sans le signe) reste dans la bulle :
// affiché en « ± », il se lisait comme une marge d'erreur du grand chiffre.
const DURATIONS = [
  ["route", "Route par trajet", "trajets", "de l'étape précédente à l'arrivée, sans hors plan ni dîner", "prévu 40 min, réel 52 min : +12 min"],
  ["loading", "Chargement", "chargements", "temps sur place moyen", "prévu 45 min, réel 1 h : +15 min"],
  ["delivery", "Livraison", "livraisons", "temps sur place moyen", "prévu 30 min, réel 25 min : −5 min"],
];

function durationCells(timing, baseline, tolerance) {
  const mean = (d) => (d?.count ? { plan: Math.round(d.planned), real: Math.round(d.real), spread: d.spread } : null);
  const gap = (m) => fmtDelta(m.real - m.plan);
  const near = `à ${toleranceLabel(tolerance).replace("± ", "")} près`;
  return DURATIONS.map(([key, label, unit, calc, example]) => {
    const d = timing?.durations?.[key];
    const now = mean(d);
    const before = mean(baseline?.durations?.[key]);
    return {
      key, label,
      value: !now ? "—" : before && gap(before) !== gap(now) ? `${gap(before)} → ${gap(now)}` : gap(now),
      sub: now ? [{ text: `${fmtDuration(now.plan)} prévues → ${fmtDuration(now.real)}` }, { text: `${d.near_pct} % ${near}` }] : [{ text: "aucune mesure" }],
      hover: [["Calcul", calc], now ? ["Mesurés", `${d.count} ${unit} sur ${d.of}`] : null,
        now ? [near.replace("à", "À"), `${d.near_pct} % (${d.near} sur ${d.count})`] : null,
        now ? ["Écart typique", `${fmtDuration(now.spread)} par visite, sans le signe (+30 et −30 font 30)`] : null,
        ["Exemple", example]],
    };
  });
}

function dailyRouteCell(route) {
  const measured = Boolean(route?.count);
  const planned = Math.round(route?.planned ?? 0);
  const real = Math.round(route?.real ?? 0);
  return {
    key: "route", label: "Route · total flotte",
    value: measured ? fmtDelta(real - planned) : "—",
    sub: [{ text: measured ? `${fmtDuration(planned)} prévues → ${fmtDuration(real)}` : "aucun camion mesuré" },
      ...(measured && route.in_progress ? [{ text: "provisoire" }] : [])],
    hover: [["Portée", "total des camions comparés de la journée sélectionnée"],
      ["Prévu", "somme du temps de route prévu de ces camions, jusqu'au même nombre de sites que le réel"],
      ["Réel", "temps de route cumulé, y compris les détours et les trajets hors ordre"],
      ["Exclus", "temps sur place, arrêts au dépôt, autres arrêts et dîner"],
      measured ? ["Camions mesurés", `${route.count} sur ${route.of}`] : null,
      measured && route.in_progress ? ["En cours", `${route.in_progress} tournée(s) : prévu et réel jusqu'au dernier site atteint`] : null],
  };
}

export function planBarCells(bar, scope, { timing = null, baseline = null, tolerance = 15 } = {}) {
  const period = scope === "period";
  const weeks = period ? bar.weeks || [] : [];
  const series = (fn) => (weeks.length > 1 ? weeks.map(fn) : null);
  const h = bar.hours;
  const stops = combinedStops(bar);
  return [
    {
      key: "stops", label: "Arrêts faits", value: share(stops, true),
      sub: [{ text: stops.planned ? `${plural(stops.planned - stops.done, "non fait")} sur ${stops.planned}` : "aucun prévu" }],
      weeks: series((w) => { const s = combinedStops(w); return pct(s.done, s.planned); }), unit: "%",
      hover: [["Calcul", "(chargements faits + livraisons faites) / (chargements prévus + livraisons prévues)"],
        ["Moyenne", "pondérée par le nombre d'arrêts prévus"], ["Ordre", "pas compté, voir Ordre respecté"],
        ["Exemple", "9 chargements sur 10 et 18 livraisons sur 20 : 27 sur 30, soit 90 %"]],
    },
    {
      key: "order", label: "Ordre respecté", value: share({ planned: bar.order.count, done: bar.order.ok }, period),
      sub: [{ text: period ? `${bar.order.ok} camions-jours sur ${bar.order.count}` : "camions" }],
      weeks: series((w) => pct(w.order.ok, w.order.count)), unit: "%",
      hover: [["Calcul", "arrêts faits dans l'ordre du plan"], ["Exemple", "prévu 1, 2, 3, fait 1, 3, 2 : hors ordre"]],
    },
    ...durationCells(timing, baseline, tolerance).map((cell) => !period && cell.key === "route" ? dailyRouteCell(bar.route_total) : cell),
    {
      key: "off", label: "Hors plan", value: bar.truck_days ? fmtDuration(perDay(bar.off_minutes, bar.truck_days)) : "—",
      sub: [{ text: `par ${period ? "camion-jour" : "camion"}, sans dîner` }],
      weeks: series((w) => perDay(w.off_minutes, w.truck_days)), unit: "min",
      hover: [["Valeur", "moyenne par camion-jour"], ["Calcul", "arrêts de 10 min et plus hors du plan"], ["Inclus", "passages au dépôt"], ["Exclus", "dîner, changement de camion"]],
    },
    {
      key: "hours", label: "Heures travaillées", value: h.days ? fmtDelta(hoursGap(h)) : "—",
      sub: [{ text: h.days ? `${fmtDuration(h.planned / h.days)} prévues → ${fmtDuration(h.real / h.days)}` : "aucune journée complète" }],
      weeks: series((w) => hoursGap(w.hours) ?? 0), unit: "delta",
      hover: [["Prévu", "plan de clôture : début, inspection comprise, jusqu'au retour au dépôt"],
        ["Réel", "début de service du carnet de route jusqu'au retour au dépôt"], ["Sans carnet", "départ réel moins l'inspection prévue"],
        ["Journées", "complètes seulement : tout le travail fait"],
        ["Exemple", "9 h 11 prévues, 9 h 55 réelles : +44 min"], h.days ? ["À +30 min", `${h.over} sur ${h.days} journées`] : null],
    },
  ];
}

// Ligne du dessous : Chargés PM, préchargements, P1 et P2. Seul un manque se lit à côté du chiffre (tone), le reste dans la bulle.
export function planBarMore(bar) {
  const [before, after] = bar.windows?.P1 || [30, 15];
  const p1 = bar.p1;
  const p2 = bar.p2;
  const p1Parts = [p1.early && { tone: "early", text: `${p1.early} en avance` }, p1.late && { tone: "late", text: `${p1.late} en retard` },
    p1.missing && { tone: "late", text: plural(p1.missing, "non faite") }].filter(Boolean);
  return [
    {
      key: "pm", label: "Chargés PM faits", value: share(bar.pm, false),
      sub: bar.pm.planned > bar.pm.done ? [{ text: plural(bar.pm.planned - bar.pm.done, "non fait"), tone: "late" }] : [],
      hover: [["Calcul", "chargement de fin de journée pour le lendemain, trouvé au lieu prévu"], ["Exemple", "Meunerie A prévue à 16:00, arrêt Geotab à la Meunerie A : fait"]],
    },
    {
      key: "preloads", label: "Préchargements faits",
      value: bar.preloads.days ? `${bar.preloads.days - bar.preloads.reloaded} / ${bar.preloads.days}` : "—",
      sub: bar.preloads.reloaded ? [{ text: `${plural(bar.preloads.reloaded, "rechargé")} le matin`, tone: "late" }] : [],
      hover: [["Fait", "camion parti chargé la veille, droit vers ses livraisons"], ["Rechargé", "arrêt au lieu de chargement avant de livrer ces commandes"],
        bar.preloads.days ? ["Commandes", `${bar.preloads.orders} de la veille`] : null],
    },
    {
      key: "p1", label: "P1 à l'heure", value: p1.count ? `${p1.ok} / ${p1.count}` : "—", sub: p1Parts,
      hover: [["Fenêtre", `${before} min avant à ${after} min après l'heure demandée`], ["Exemple", `07:30 demandée : arrivée de ${clock(450 - before)} à ${clock(450 + after)}`]],
    },
    {
      key: "p2", label: "P2 dans la fenêtre", value: p2.count ? `${p2.ok} / ${p2.count}` : "—", sub: [],
      hover: p2.count ? [["Fenêtre", `± ${bar.windows?.P2?.[1] ?? 60} min de l'heure demandée`], ["En avance", `${p2.early}`], ["En retard", `${p2.late}`], ["Non faites", `${p2.missing}`]] : null,
    },
  ];
}

// Valeurs des semaines dans la bulle d'une case : « 90 % · 73 % · … ».
export function weekText(values, unit) {
  const one = (v) => (v == null ? "—" : unit === "%" ? `${v} %` : unit === "delta" ? fmtDelta(v) : fmtDuration(v));
  return values.map(one).join(" · ");
}

function clock(minutes) {
  return `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
}
